import Wobs from '../wobs'

const distributionTypes = {
  horizontal: {
    baseline: 'center',
    adjust: 'left',
    min: {
      primary: 'left',
      secondary: 'top'
    },
    max: {
      primary: 'right',
      secondary: 'bottom'
    }
  },
  vertical: {
    baseline: 'middle',
    adjust: 'top',
    min: {
      primary: 'top',
      secondary: 'left'
    },
    max: {
      primary: 'bottom',
      secondary: 'right'
    }
  }
}

/**
 * Arranger component for positioning and aligning wobs
 */
class Arranger {

  /**
   * Creates a new Arranger instance on a group of incoming wobs.
   * @constructor
   * @param {Array} wobs
   * @param {Function} movable a function that determines if the wob can
   *  be moved. By default set to a function that always returns true
   */
  constructor (wobs = [], movable = () => true) {
    this.wobs = new Wobs(wobs)
    this.movable = movable
  }

  align (position) {
    if (this.wobs.length < 2) {
      return
    }

    var box = this.wobs.boundingBox()
    box.center = box.left + ((box.right - box.left) / 2)
    box.middle = box.top + ((box.bottom - box.top) / 2)

    this.wobs.filter(this.movable).forEach(wob => {
      var to = {}
      if (position === 'top' || position === 'left') {
        to[position] = box[position]
      } else if (position === 'right') {
        to['left'] = box.right - wob.get('width')
      } else if (position === 'bottom') {
        to['top'] = box.bottom - wob.get('height')
      } else if (position === 'center') {
        to['left'] = box.center - wob.area().width / 2
      } else if (position === 'middle') {
        to['top'] = box.middle - wob.area().height / 2
      }
      wob.move(to)
    })
  }

  /*
   * Determines which wob in the arranger's `wobs` are at the min/max
   * of the wob's bounding box using the properties defined in `bounds`
   */
  _extremum (bounds) {
    let boundingBox = this.wobs.boundingBox()
    return Object.keys(bounds).reduce((p, extent) => {
      let matches = this.wobs.filter(wob => {
          return wob.boundingBox()[bounds[extent].primary] === boundingBox[bounds[extent].primary]
        })
        , sortOrder = extent === 'max' ? -1 : 1

      // See if there's just a single wob at the bounds of the bounding box
      if (matches.length > 1) {
        // Sort the matches based on the secondary coordinate
        matches.sort((a, b) => (a.boundingBox()[bounds[extent].secondary] - b.boundingBox()[bounds[extent].secondary]) * sortOrder)
      }
      p[extent] = matches[0]
      return p
    }, {})
  }

  /*
   * Evenly distributes the wobs to the incoming `alignment`,
   * which can be `vertical` or `horizontal`.
   */
  distribute (alignment) {
    if (this.wobs.length < 3) {
      return
    }

    /* We need to determine the anchors. In most cases, this is just the wobs
     * that are part of the extremes of the bounding box. i.e. leftmost, rightmost, topmost, bottommost.
     * If there ties, meaning multiple wobs at the same coordinate, we use the the top-leftmost and bottom-rightmost
     * wobs.
     */
    let distribution = distributionTypes[alignment]
      , anchors = this._extremum(distribution)
      , baseline = distribution.baseline
      , adjust = distribution.adjust
      , inbetween = this.wobs.toArray().sort((a, b) => {
                                // Sort the collection by the baseline so wobs don't swap positions
                                return a.boundingBox()[baseline] - b.boundingBox()[baseline]
                              }).filter(wob => {
                                // Find all the wobs that aren't an anchor
                                return wob !== anchors.min && wob !== anchors.max
                              })
      , min = anchors.min.boundingBox()[baseline]
      , max = anchors.max.boundingBox()[baseline]
      , range = Math.abs(max - min)
      , spacing = range / (inbetween.length + 1)

    inbetween.filter(this.movable).forEach((wob, i) => {
      let pos = min + spacing * (i + 1)
        , delta = pos - wob.boundingBox()[baseline]
        , to = {}
      to[adjust] = wob.get(adjust) + delta

      wob.move(to)
    })
  }

}

export default Arranger
